import { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send, ShoppingCart } from "lucide-react";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { BRAND } from "@/lib/brand";
import { toast } from "sonner";

export default function ChatWidget() {
  const { addItem } = useCart();
  const [open, setOpen] = useState(false);
  const [sessionId] = useState(() => Math.random().toString(36).slice(2));
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hi! Looking for something specific today?" },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    const next = [...messages, { role: "user", content: text }];
    setMessages(next);
    setInput("");
    setLoading(true);
    try {
      const { data } = await api.post("/chat", {
        session_id: sessionId,
        messages: next.filter((m) => m.role !== "system").map((m) => ({ role: m.role, content: m.content })),
      });
      setMessages((prev) => [...prev, { role: "assistant", content: data.reply, product: data.product }]);
    } catch {
      setMessages((prev) => [...prev, { role: "assistant", content: "Hmm, I glitched for a sec. Try again or email ape-tides.support@ape-tides.com." }]);
    } finally { setLoading(false); }
  };

  return (
    <>
      {/* Bubble */}
      <button onClick={() => setOpen(!open)} data-testid="chat-bubble"
        className="fixed bottom-5 right-5 z-40 h-14 w-14 rounded-full bg-ape-red hover:bg-ape-redhover text-white flex items-center justify-center shadow-lg shadow-black/40 transition-colors duration-150">
        {open ? <X size={24} /> : <MessageCircle size={26} />}
      </button>

      {/* Panel */}
      {open && (
        <div className="fixed bottom-24 right-5 z-40 w-[92vw] max-w-sm h-[70vh] max-h-[560px] bg-ape-surface border border-ape-border shadow-2xl shadow-black/50 flex flex-col" data-testid="chat-panel">
          <div className="flex items-center gap-3 p-4 border-b border-ape-border bg-ape-bg">
            <img src={BRAND.logoCircle} alt="" className="h-9 w-9 object-contain" />
            <div>
              <div className="font-display text-lg tracking-wide leading-none">APE ASSISTANT</div>
              <div className="font-mono2 text-[10px] tracking-widest text-ape-muted">USUALLY REPLIES INSTANTLY</div>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] px-3.5 py-2.5 text-sm leading-relaxed ${m.role === "user" ? "bg-ape-red text-white" : "bg-ape-bg border border-ape-border text-white/90"}`} data-testid={`chat-msg-${m.role}`}>
                  <span dangerouslySetInnerHTML={{ __html: m.content.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>") }} />
                  {m.product && (
                    <button onClick={() => { addItem(m.product); toast.success(`${m.product.name} added to cart`); }}
                      data-testid={`chat-add-${m.product.slug}`}
                      className="mt-2 w-full bg-ape-red hover:bg-ape-redhover text-white font-head uppercase tracking-widest text-xs py-2 flex items-center justify-center gap-2 transition-colors duration-150">
                      <ShoppingCart size={13} /> Add {m.product.name} · ${m.product.price.toFixed(0)}
                    </button>
                  )}
                </div>
              </div>
            ))}
            {loading && <div className="text-ape-muted font-mono2 text-xs">APE is typing…</div>}
            <div ref={endRef} />
          </div>

          <div className="p-3 border-t border-ape-border flex gap-2">
            <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Ask about peptides, affiliates…" data-testid="chat-input"
              className="flex-1 bg-ape-bg border border-ape-border rounded-none px-3 py-2.5 text-sm text-white focus:border-ape-red focus:outline-none transition-colors duration-150" />
            <button onClick={send} disabled={loading} data-testid="chat-send" className="bg-ape-red hover:bg-ape-redhover disabled:opacity-50 text-white px-4 transition-colors duration-150">
              <Send size={16} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
